import * as React from 'react';
import { Command } from 'cmdk';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { County } from '@/lib/api';
import { Badge, Button } from './ui/primitives';
import { Popover, PopoverContent, PopoverTrigger } from './ui/overlays';
import { STATE_NAMES } from '@shared/counties/index.js';

const STATE_LABELS: Record<string, string> = STATE_NAMES;

/**
 * County selector for the supported multi-state catalogue.
 *
 * A native <select> handles six options fine and eighty-eight badly, and a
 * column of eighty-eight checkboxes is worse. This is the pattern already used
 * by the command palette: type to filter, arrow keys to move, Enter to choose.
 *
 * Everything stays keyboard-reachable — cmdk owns the arrow keys and Enter
 * inside the list, the trigger is a real button, and Escape closes the popover
 * and returns focus to it. Nothing here needs a mouse.
 */

interface BaseProps {
  counties: County[] | undefined;
  placeholder?: string;
  disabled?: boolean;
  /** Rendered under the control; use it to explain what the county means here. */
  hint?: string;
}

interface SingleProps extends BaseProps {
  multiple?: false;
  value: number | null;
  onChange: (value: number | null) => void;
}

interface MultiProps extends BaseProps {
  multiple: true;
  value: number[];
  onChange: (value: number[]) => void;
}

export function CountyPicker(props: SingleProps | MultiProps) {
  const { counties, disabled, hint } = props;
  const [open, setOpen] = React.useState(false);
  const list = counties ?? [];
  const byId = React.useMemo(() => new Map(list.map((c) => [c.id, c])), [list]);

  // Counties grouped by state, in catalogue order, so Ohio and Michigan are
  // visibly separate sections rather than one long interleaved list.
  const grouped = React.useMemo(() => {
    const map = new Map<string, County[]>();
    for (const county of list) {
      const state = county.state ?? '';
      map.set(state, [...(map.get(state) ?? []), county]);
    }
    return [...map.entries()];
  }, [list]);

  /**
   * Names that appear in more than one state present in this list. Only these
   * get a state suffix — labelling all 171 would be noise, and labelling none
   * is how someone calls the wrong Monroe.
   */
  const sharedNames = React.useMemo(() => {
    const states = new Map<string, Set<string>>();
    for (const county of list) {
      const key = county.name.toLowerCase();
      states.set(key, (states.get(key) ?? new Set()).add(county.state ?? ''));
    }
    return new Set([...states].filter(([, s]) => s.size > 1).map(([name]) => name));
  }, [list]);

  const isShared = (name: string) => sharedNames.has(name.toLowerCase());
  const qualified = (county: County | undefined): string => {
    if (!county) return 'Unknown county';
    return isShared(county.name) ? `${county.name} (${county.state})` : county.name;
  };

  const selectedIds = props.multiple ? props.value : props.value === null ? [] : [props.value];

  const toggle = (id: number) => {
    if (props.multiple) {
      props.onChange(
        props.value.includes(id) ? props.value.filter((v) => v !== id) : [...props.value, id],
      );
      return;
    }
    // Choosing the same county again clears it, which is how the old native
    // select's blank option behaved.
    props.onChange(props.value === id ? null : id);
    setOpen(false);
  };

  const label = (() => {
    if (!selectedIds.length) return props.placeholder ?? 'Any county';
    if (!props.multiple) return qualified(byId.get(selectedIds[0]));
    if (selectedIds.length === 1) return qualified(byId.get(selectedIds[0]));
    return `${selectedIds.length} counties`;
  })();

  return (
    <div className="space-y-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            aria-label={props.placeholder ?? 'Select county'}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className={cn('truncate', !selectedIds.length && 'text-muted-foreground')}>
              {label}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command
            // Filter on the name only. FIPS codes are in the data but nobody
            // searches a county by its federal code.
            filter={(value, search) =>
              value.toLowerCase().includes(search.toLowerCase().trim()) ? 1 : 0
            }
          >
            <Command.Input
              placeholder="Search counties…"
              className="w-full border-0 border-b border-border bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-muted-foreground"
            />
            <Command.List className="max-h-64 overflow-y-auto p-1">
              <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
                No county matches that.
              </Command.Empty>
              {!props.multiple && (
                <Command.Item
                  value="__any__ any county none clear"
                  onSelect={() => {
                    props.onChange(null);
                    setOpen(false);
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground aria-selected:bg-accent"
                >
                  <span className="w-4" />
                  {props.placeholder ?? 'Any county'}
                </Command.Item>
              )}
              {grouped.map(([state, counties]) => (
                <Command.Group
                  key={state}
                  heading={STATE_LABELS[state] ?? state}
                  className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:text-muted-foreground"
                >
                  {counties.map((county) => {
                const checked = selectedIds.includes(county.id);
                return (
                  <Command.Item
                    key={county.id}
                    // The state is part of the searchable value AND the label,
                    // because eight names exist in both states and picking the
                    // wrong Monroe is not a recoverable mistake.
                    value={`${county.name} ${county.state}`}
                    onSelect={() => toggle(county.id)}
                    className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm aria-selected:bg-accent"
                  >
                    <Check className={cn('h-4 w-4 shrink-0', checked ? 'opacity-100' : 'opacity-0')} />
                    <span className="flex-1 truncate">
                      {county.name}
                      {isShared(county.name) && (
                        <span className="ml-1 text-muted-foreground">({county.state})</span>
                      )}
                    </span>
                    {typeof county.leadCount === 'number' && county.leadCount > 0 && (
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {county.leadCount}
                      </span>
                    )}
                  </Command.Item>
                );
                  })}
                </Command.Group>
              ))}
            </Command.List>
          </Command>
        </PopoverContent>
      </Popover>

      {props.multiple && selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedIds.map((id) => (
            <Badge key={id} variant="outline" className="gap-1 pr-1">
              {byId.get(id)?.name ?? `#${id}`}
              <button
                type="button"
                aria-label={`Remove ${qualified(byId.get(id))}`}
                onClick={() => toggle(id)}
                className="rounded-sm p-0.5 hover:bg-background/60"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
